import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { authEnabled } from "../AuthGate";
import { fetchMembership, NO_MEMBERSHIP, type Membership } from "../../sim/membership";

/**
 * IS THIS VISITOR A GARDEN CLUB MEMBER? — the one read every site surface uses to pick its version: the nav
 * pill, the home page's club band and top button, the Profile panel, and /garden-club itself. A member never
 * sees a pitch to join; everyone else never sees member copy.
 *
 * ONE FETCH PER PAGE LOAD per sign-in state, shared by every component that asks (as `useGardenSummary`
 * shares the cloud save read). `loading` stays true until Clerk has decided who this is AND the answer for
 * that person is in — so the UI can hold its club slot empty instead of flashing the pitch at a member.
 */

let cache: { signedIn: boolean; m: Membership } | null = null;
let inflight: { signedIn: boolean; p: Promise<Membership> } | null = null;
const listeners = new Set<(c: { signedIn: boolean; m: Membership }) => void>();

function load(signedIn: boolean, force = false): Promise<Membership> {
  if (!force && inflight && inflight.signedIn === signedIn) return inflight.p;
  const p = fetchMembership().then((m) => {
    cache = { signedIn, m };
    for (const l of listeners) l(cache);
    return m;
  });
  inflight = { signedIn, p };
  return p;
}

/** Clerk's verdict, or "known, signed out" in a build without auth. `authEnabled` is a build-time constant,
 *  so the hook runs the same way on every render. */
function useSignedIn(): { known: boolean; signedIn: boolean } {
  if (!authEnabled) return { known: true, signedIn: false };
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isLoaded, isSignedIn } = useAuth();
  return { known: !!isLoaded, signedIn: !!isSignedIn };
}

export function useMembership(): { m: Membership; member: boolean; loading: boolean; refresh: () => Promise<Membership> } {
  const { known, signedIn } = useSignedIn();
  const [state, setState] = useState(cache);
  useEffect(() => {
    listeners.add(setState);
    return () => { listeners.delete(setState); };
  }, []);
  useEffect(() => {
    if (!known) return;
    if (cache?.signedIn !== signedIn) void load(signedIn);
    else setState(cache);
  }, [known, signedIn]);
  const refresh = useCallback(() => load(signedIn, true), [signedIn]);
  const fresh = known && state !== null && state.signedIn === signedIn;
  const m = fresh ? state!.m : NO_MEMBERSHIP;
  return { m, member: m.status === "active", loading: !fresh, refresh };
}

/** "October 31, 2026" — the one date format the club's copy uses. */
export function clubDate(ms: number | undefined): string {
  if (!ms) return "";
  return new Date(ms).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}
